import {
  type AssignRiskInstancesResult,
  type BulkCreateInstanceMeasureLinksResult,
  type CreateRiskInstancesFromGenerationResult,
  type GenerateRiskInstancesPreviewResult,
  type RiskActivityEntry,
} from "@pima/web-api/client";
import { Array as Arr, Data, Effect, Either } from "effect";
import { runIntent, runIntentResult } from "../run-intent";
import type { Payload } from "../contract-types";
import { getClient } from "./client";

// ===========================================================================
// admin-risk-actions — the risk-authoring custom actions that don't map onto
// a single table's CRUD: previewing/generating risk instances from the
// shared catalog for one or more locations (or company-wide), the
// translation-group-wide assign and bulk-measure-link flows, and the history
// every risk panel shows.
// ===========================================================================

export type AvailableRiskInput = {
  id: string;
  sourceType: "template" | "instance";
};

function toGenerateRiskInstancesPayload(params: {
  availableRisks: readonly AvailableRiskInput[];
  locationIds: readonly string[];
  isCompanyWide: boolean;
  clientId: string;
  language: Payload<
    "admin-risk-actions",
    "generateRiskInstancesPreview"
  >["language"];
}) {
  const availableRiskIds = params.availableRisks.map((r) => ({
    id: r.id,
    source_type: r.sourceType,
  }));
  if (!Arr.isNonEmptyReadonlyArray(availableRiskIds)) {
    throw new Error("Generating risk instances needs at least one risk");
  }
  return {
    available_risk_ids: availableRiskIds,
    location_ids: [...params.locationIds],
    is_company_wide: params.isCompanyWide,
    client_id: params.clientId,
    language: params.language,
  };
}

// Previews the risk instances a generate action would create, without
// writing anything — the generation modal's live-updating table as the
// caller changes locations/company-wide.
export async function generateRiskInstancesPreview(params: {
  availableRisks: readonly AvailableRiskInput[];
  locationIds: readonly string[];
  isCompanyWide: boolean;
  clientId: string;
  language: Payload<
    "admin-risk-actions",
    "generateRiskInstancesPreview"
  >["language"];
}): Promise<GenerateRiskInstancesPreviewResult> {
  const client = await getClient();
  const { data } = await runIntent(
    client["admin-risk-actions"].generateRiskInstancesPreview({
      payload: toGenerateRiskInstancesPayload(params),
    }),
  );
  return data;
}

// Actually creates the risk instances the preview described, for the same
// available-risk/location selection.
export async function createRiskInstancesFromGeneration(params: {
  availableRisks: readonly AvailableRiskInput[];
  locationIds: readonly string[];
  isCompanyWide: boolean;
  clientId: string;
  language: Payload<
    "admin-risk-actions",
    "createRiskInstancesFromGeneration"
  >["language"];
}): Promise<CreateRiskInstancesFromGenerationResult> {
  const client = await getClient();
  const { data } = await runIntent(
    client["admin-risk-actions"].createRiskInstancesFromGeneration({
      payload: toGenerateRiskInstancesPayload(params),
    }),
  );
  return data;
}

// Assigns every language row of the translation groups to the client, with
// the purpose and description the assigner gave.
export async function assignRiskInstances(params: {
  translationGroupIds: Arr.NonEmptyReadonlyArray<number>;
  assignmentPurpose: string;
  assignmentDescription: string;
}): Promise<AssignRiskInstancesResult> {
  const client = await getClient();
  const { data } = await runIntent(
    client["admin-risk-actions"].assignRiskInstances({
      payload: {
        translation_group_ids: params.translationGroupIds,
        assignment_purpose: params.assignmentPurpose,
        assignment_description: params.assignmentDescription,
      },
    }),
  );
  return data;
}

// Links every risk instance to every measure template of its language; the
// server skips pairs already linked and drafts the measure each new link
// calls for.
export async function bulkCreateInstanceMeasureLinks(params: {
  riskInstanceIds: Arr.NonEmptyReadonlyArray<string>;
  measureTemplateIds: Arr.NonEmptyReadonlyArray<string>;
}): Promise<BulkCreateInstanceMeasureLinksResult> {
  const client = await getClient();
  const { data } = await runIntent(
    client["admin-risk-actions"].bulkCreateInstanceMeasureLinks({
      payload: {
        risk_instance_ids: params.riskInstanceIds,
        measure_template_ids: params.measureTemplateIds,
      },
    }),
  );
  return data;
}

type ActionsClient = Awaited<
  ReturnType<typeof getClient>
>["admin-risk-actions"];
class RiskActivityRequestError extends Data.TaggedError(
  "RiskActivityRequestError",
)<{ readonly cause: unknown }> {}
export type RiskActivityError =
  | Effect.Effect.Error<
      | ReturnType<ActionsClient["getRiskTemplateActivity"]>
      | ReturnType<ActionsClient["getRiskInstanceActivity"]>
    >
  | RiskActivityRequestError;

/** One page of history, preserving expected contract failures as values. */
export async function getRiskActivity(params: {
  kind: "templates" | "instances";
  id: string;
  limit: number;
  offset: number;
}): Promise<Either.Either<readonly RiskActivityEntry[], RiskActivityError>> {
  try {
    const client = await getClient();
    const urlParams = {
      id: params.id,
      limit: params.limit,
      offset: params.offset,
    };
    const result = await runIntentResult(
      params.kind === "templates"
        ? client["admin-risk-actions"].getRiskTemplateActivity({ urlParams })
        : client["admin-risk-actions"].getRiskInstanceActivity({ urlParams }),
    );
    return Either.map(result, ({ data }) => data);
  } catch (cause) {
    return Either.left(new RiskActivityRequestError({ cause }));
  }
}
